import * as React from 'react';
import { Navigate } from 'react-router-dom';
import { SurveyActivityListing, rng, ActivityType, assertNever } from '../../amorg-common';
import { getSurveyActivities, createActivity } from '../../api/activityClientApi';
import { Button } from '../../designSystem/button/Button';
import { LinkButton } from '../../designSystem/button/LinkButton';
import { colors } from '../../designSystem/colors/color';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { Icon } from '../../designSystem/icons/Icons';
import { SingleColumnLayout } from '../../designSystem/layouts/SingleColumnLayout';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { PageHeader } from '../../designSystem/PageHeader';
import { PageSectionTable } from '../../designSystem/PageSectionTable';
import { txt } from '../../txt';
import * as dateMath from 'date-arithmetic';
import { ProtectedPageWrapper } from '../../designSystem/layouts/pageWrappers/ProtectedPageWrapper';
import { toDateString } from '../../utils/date';

export default function WorkplaceAssessmentListingPage(): JSX.Element 
{

	return (
		<ProtectedPageWrapper requiredPermissions={['ViewActivity']} selectedMenuItemKey="workplaceassessment">
            <WorkplaceAssessmentListingView />
		</ProtectedPageWrapper>
	);
}


type State = {
    loading:boolean,
    activities:SurveyActivityListing[],
    redirectId:string
}
function WorkplaceAssessmentListingView(): JSX.Element
{
    const [state, setState] = React.useState<State>({
        loading:true,
        activities:[],
        redirectId:''
    });

    React.useEffect(()=>{
        const getData = async () => {
            const activities = await getSurveyActivities('WorkplaceAssessment');
            activities.sort((a,b)=> b.start.getTime() - a.start.getTime());
            // Get activities convert start and end from strings (JSON transport) to dates
            setState({activities, loading:false, redirectId:''});
        }
        getData();
    },[])

    async function handleClickNew()
    {
        const now = new Date();

        const activityId = rng.getRandomId();
        const startDate = new Date(now.getFullYear(), now.getMonth()+1, 1, 8, 0);
        const endDate = dateMath.add(startDate, 1, 'month');
        const type:ActivityType = 'WorkplaceAssessment';

        setState({...state, loading:true});

        const result = await createActivity(
            activityId,
            'WorkplaceAssessment',
            txt.getDynamic(`NewActivityType_${type}`),
            '',
            startDate,
            endDate,
            ''
        );

        if(result.isSuccess){
            setState({...state, redirectId:activityId});
        }
    }

    const current = state.activities?.filter(a => a.end >= new Date()) || [];
    const past = state.activities?.filter(a => a.end < new Date()) || [];

    return (
        <SingleColumnLayout
            noDivider
            contentContainer={
                <>
                    {state.redirectId !== '' &&
                        <Navigate to={`/workplaceassessments/${state.redirectId}`} />
                    }

                    <LoadingPageOverlay loading={state.loading} />

                    <PageHeader
                        headerText="Aktuelle APV'er"
                        headerStyle="primary-black"
                        headerIcon="interface_user_userOK"
                        buttons={
                            <Button variant="filled" text="Opret ny APV" icon="actions_plus" onClick={handleClickNew} />
                        }
                    />

                    <PageSectionTable
                        headerColumns={[
                            {text:'Periode', gridDefinition:'minmax(0, 1fr)'},
                            {text:'APV', gridDefinition:'minmax(0, 1fr)'},
                            {text:'Status', gridDefinition:'minmax(0, 1fr)'},
                            {text:'', gridDefinition:'min-content'}
                        ]}
                        noContentText="Ingen aktuelle APV'er"
                        showAllLimit={5}>

                        {current.map(activity => {
                            

                            return (
                                <>
                                    <PeriodContent activity={activity} />

                                    <div>{activity.title}</div>

                                    <CurrentSurveyStatus activity={activity} />

                                    <LinkButton variant="text"
                                        icon="arrows_chevron_chevronForward"
                                        href={`/workplaceassessments/${activity.id}`} />
                                </>
                            );
                        })}
                    </PageSectionTable>

                    {past.length > 0 &&

                        <>
                            <PageHeader
                                headerText="Tidligere APV'er"
                                headerStyle="primary-black"
                            />

                            <PageSectionTable
                                headerColumns={[
                                    {text:'Periode', gridDefinition:'20%'},
                                    {text:'APV', gridDefinition:'minmax(0, 1fr)'},
                                    {text:'Status', gridDefinition:'minmax(0, 1fr)'},
                                    {text:'', gridDefinition:'min-content'}
                                ]}
                                noContentText="Ingen tidligere APV'er"
                                showAllLimit={5}>

                                {past.map(activity => {
                                    

                                    return (
                                        <>
                                            <PeriodContent activity={activity} />

                                            <div>{activity.title}</div>

                                            <PastSurveyStatus activity={activity} />

                                            <LinkButton variant="text"
                                                icon="arrows_chevron_chevronForward"
                                                href={`/workplaceassessments/${activity.id}`} />
                                        </>
                                    );
                                })}
                            </PageSectionTable>
                        </>
                    }
                </>
            }
        />
    );
}

function PeriodContent({activity}:{activity:SurveyActivityListing}):JSX.Element
{
    return <span>{toDateString(activity.start) + ' - ' + toDateString(activity.end)}</span>;
}

function CurrentSurveyStatus({activity}:{activity:SurveyActivityListing}):JSX.Element
{    
    const status = surveyStatus(activity);
    if(status === 'no-survey')
        return <Flex flexDirection="row" alignItems="center" gap={4}>Intet spørgeskema</Flex>;
    if(status === 'waiting-to-open')
        return <Flex flexDirection="row" alignItems="center" gap={4}><Icon name="fact_check" color={colors.icon.active} />Planlagt ({activity.survey?.questionCount} spørgsmål) </Flex>;
    else if(status === 'closed')
        return <Flex flexDirection="row" alignItems="center" gap={4}><Icon name="status_ok" color={colors.icon.success} />Spørgeskema afsluttet ({activity.survey?.answerCount || 0} besvarelser) </Flex>;
    else if(status === 'open')
        return <Flex flexDirection="row" alignItems="center" gap={4}><Icon name="interface_glyph_hourglass" color={colors.icon.success} />Spørgeskema åbent ({activity.survey?.answerCount || 0} besvarelser) </Flex>;
    else
        assertNever(status);
}

function PastSurveyStatus({activity}:{activity:SurveyActivityListing}):JSX.Element
{
    if(activity.actionPlanCount === 0)
        return <Flex flexDirection="row" alignItems="center" gap={4}>Ingen handleplaner</Flex>;
    else if(activity.actionPlanCount === 1)
        return <Flex flexDirection="row" alignItems="center" gap={4}><Icon name="status_ok" color={colors.icon.success} />1 handleplan oprettet</Flex>;
    else
        return <Flex flexDirection="row" alignItems="center" gap={4}><Icon name="status_ok" color={colors.icon.success} />{activity.actionPlanCount} handleplaner oprettet</Flex>;
}

const surveyStatus = (activity:SurveyActivityListing): 'no-survey' | 'waiting-to-open' | 'open' | 'closed' => {
    if(activity.survey === null)
        return 'no-survey';
    const now = new Date();
    if(activity.survey.start > now)
        return 'waiting-to-open';
    if(activity.survey.end < now)
        return 'closed';
    return 'open';
}